import { ModelDataType } from 'src/app/interfaces/model-data-type';
import { Site } from 'src/app/interfaces/models/site';
import { State } from 'src/app/interfaces/state';

import { Address } from './address';
import { BaseModel } from './base-model';
import { ClientRegisteredVendor } from './client-registered-vendor';
import { Company } from './company';
import { ExemptVendor } from './exempt-vendor';
import { User } from './user';
import { Vendor } from './vendor';

export class Contact extends BaseModel {
  static override columnMap = new Map<
    string,
    { property: string; type: 'column'; dataType: ModelDataType }
  >();
  static override associationMap = new Map<
    string,
    { property: string; type: 'association'; model: string }
  >();
  static override aliasedColumnMap = new Map<
    string,
    { property: string; type: 'alias' }
  >();

  static ContactProperties: (
    | {
        property: keyof Contact;
        type: 'column';
        dataType: ModelDataType;
      }
    | {
        property: keyof Contact;
        type: 'alias';
      }
    | {
        property: keyof Contact;
        type: 'association';
        model: string;
      }
  )[] = [
    { property: 'id', type: 'column', dataType: 'number' },
    { property: 'email', type: 'column', dataType: 'string' },
    { property: 'firstName', type: 'column', dataType: 'string' },
    { property: 'lastName', type: 'column', dataType: 'string' },
    { property: 'isPrimary', type: 'column', dataType: 'boolean' },
    { property: 'jobTitle', type: 'column', dataType: 'string' },
    { property: 'phone1', type: 'column', dataType: 'string' },
    { property: 'phone2', type: 'column', dataType: 'string' },
    { property: 'ext', type: 'column', dataType: 'string' },
    { property: 'ext2', type: 'column', dataType: 'string' },
    { property: 'address', type: 'column', dataType: 'string' },
    { property: 'address2', type: 'column', dataType: 'string' },
    { property: 'city', type: 'column', dataType: 'string' },
    { property: 'state', type: 'column', dataType: 'string' },
    { property: 'zip', type: 'column', dataType: 'string' },
    { property: 'SiteId', type: 'column', dataType: 'number' },
    { property: 'CompanyId', type: 'column', dataType: 'number' },
    { property: 'VendorId', type: 'column', dataType: 'number' },
    { property: 'ExemptVendorId', type: 'column', dataType: 'number' },
    {
      property: 'ClientRegisteredVendorId',
      type: 'column',
      dataType: 'number'
    },
    { property: 'UserId', type: 'column', dataType: 'number' },
    { property: 'isArchived', type: 'column', dataType: 'boolean' },
    { property: 'createdAt', type: 'column', dataType: 'date' },
    { property: 'updatedAt', type: 'column', dataType: 'date' },
    { property: 'isContractSite', type: 'column', dataType: 'boolean' },
    { property: 'isContractVendor', type: 'column', dataType: 'boolean' },
    { property: 'ContractId', type: 'column', dataType: 'number' },
    { property: 'invitationSendDate', type: 'column', dataType: 'date' },
    { property: 'isCompliance', type: 'column', dataType: 'boolean' },
    { property: 'isBid', type: 'column', dataType: 'boolean' },
    { property: 'isBilling', type: 'column', dataType: 'boolean' },
    { property: 'isPrimaryCompliance', type: 'column', dataType: 'boolean' },
    { property: 'isPrimaryBid', type: 'column', dataType: 'boolean' },
    { property: 'isPrimaryBilling', type: 'column', dataType: 'boolean' },

    { property: 'fullName', type: 'alias' },
    { property: 'contactTypes', type: 'alias' },

    { property: 'Company', model: 'Company', type: 'association' },
    { property: 'Vendor', model: 'Vendor', type: 'association' },
    { property: 'ExemptVendor', model: 'ExemptVendor', type: 'association' },
    {
      property: 'ClientRegisteredVendor',
      model: 'ClientRegisteredVendor',
      type: 'association'
    },
    { property: 'Site', model: 'Site', type: 'association' },
    { property: 'User', model: 'User', type: 'association' },
    { property: 'Addresses', model: 'Address', type: 'association' },
    { property: 'StreetAddress', model: 'StreetAddress', type: 'association' },
    { property: 'MailingAddress', model: 'MailingAddress', type: 'association' }
  ];

  static {
    Contact.ContactProperties.forEach((propertyObject) => {
      const { property, type } = propertyObject;
      if (type === 'column') {
        Contact.columnMap.set(property, propertyObject);
      }
      if (type === 'alias') {
        Contact.aliasedColumnMap.set(
          property,
          propertyObject as { property: string; type: 'alias' }
        );
      }
      if (type === 'association') {
        Contact.associationMap.set(
          property,
          propertyObject as {
            property: string;
            type: 'association';
            model: string;
          }
        );
      }
    });
  }

  constructor(ContactObject: Partial<Contact>) {
    super();

    Contact.ContactProperties.forEach((propertyObject) => {
      const { property } = propertyObject;
      const value = ContactObject[property];
      if (value !== undefined) {
        Object.assign(this, { [property]: value });
      }
    });
  }

  id?: number | null;
  email?: string;
  firstName?: string;
  lastName?: string;
  isPrimary?: boolean;
  jobTitle?: string | null;
  phone1?: string | null;
  phone2?: string | null;
  ext?: string | null;
  ext2?: string | null;
  address?: string;
  address2?: string | null;
  city?: string;
  state?: State;
  zip?: string;
  SiteId?: number | null;
  CompanyId?: number | null;
  VendorId?: number | null;
  ExemptVendorId?: number | null;
  ClientRegisteredVendorId?: number | null;
  UserId?: number | null;
  isArchived?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  isContractVendor?: boolean;
  isContractSite?: boolean;
  ContractId?: number | null;
  invitationSendDate?: Date | null;
  isCompliance?: boolean;
  isBid?: boolean;
  isBilling?: boolean;
  isPrimaryCompliance?: boolean;
  isPrimaryBid?: boolean;
  isPrimaryBilling?: boolean;

  // Alias column
  fullName?: string;
  contactTypes?: string;

  Company?: Company | null;
  Vendor?: Vendor | null;
  ExemptVendor?: ExemptVendor | null;
  ClientRegisteredVendor?: ClientRegisteredVendor | null;
  Site?: Site | null;
  User?: User | null;
  Addresses?: Address[];
  StreetAddress?: Address;
  MailingAddress?: Address;
}
